import { Injectable, inject } from '@angular/core';
import { AssetsService } from '@api';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApplicationState } from 'src/app/shared/state/application.state';
import '../../shared/extensions/string.extensions';
import { ResetAssetDetails } from '../asset-details.actions';
import { AssetSelectedState } from '../asset-selected-state/asset-selected.state';
import * as actions from './asset-commands.actions';
import { AssetCommandsStateModel, assetCommandsDefaults } from './asset-commands.state.model';

@State<AssetCommandsStateModel>(assetCommandsDefaults)
@Injectable()
export class AssetCommandsState {
    private assetsService = inject(AssetsService);
    private store = inject(Store);

    @Selector()
    public static getIsCommandExecuting(state: AssetCommandsStateModel) {
        return state.isCommandExecuting;
    }

    @Selector()
    public static getCommandErrors(state: AssetCommandsStateModel) {
        return state.commandErrors;
    }

    @Selector()
    public static getCommandExecutionHistorys(state: AssetCommandsStateModel) {
        return state.commandExecutionHistorys;
    }

    @Action(actions.ExecuteCommand)
    public executeCommand({ patchState, getState }: StateContext<AssetCommandsStateModel>, action: actions.ExecuteCommand) {
        const setLoading = (isEnabled: boolean) => {
            patchState({
                isCommandExecuting: {
                    ...getState().isCommandExecuting,
                    [action.command.name]: isEnabled,
                },
            });

            if (isEnabled) {
                patchState({
                    commandErrors: {
                        ...getState().commandErrors,
                        [action.command.name]: null,
                    },
                });
            }
        };

        setLoading(true);

        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);

        const deviceId = this.store.selectSnapshot(AssetSelectedState.getSelectedDeviceId);
        const assetId = this.store.selectSnapshot(AssetSelectedState.getSelectedAssetId);

        return this.assetsService.executeCommand(deviceId, assetId, tenantName, action.command).pipe(
            tap((_) => {
                setLoading(false);
            }),
            catchError((err) => {
                if (err.status === 409) {
                    patchState({
                        commandErrors: {
                            ...getState().commandErrors,
                            [action.command.name]: err?.error?.errorList?.[0],
                        },
                    });
                }

                setLoading(false);
                return throwError(err);
            })
        );
    }

    @Action(actions.GetCommandExecutionHistorys)
    public getCommandExecutionHistorys({ patchState }: StateContext<AssetCommandsStateModel>, action: actions.GetCommandExecutionHistorys) {
        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);

        return this.assetsService
            .getAssetCommandExecutionHistory(
                action.deviceId,
                action.assetId,
                tenantName,
                action.gridRequestQuery.orderBy,
                action.gridRequestQuery.orderByDirection,
                action.gridRequestQuery.filter,
                action.gridRequestQuery.page,
                action.gridRequestQuery.pageSize
            )
            .pipe(
                tap((result) => {
                    patchState({
                        commandExecutionHistorys: result,
                    });
                })
            );
    }

    @Action(ResetAssetDetails)
    public resetState(ctx: StateContext<AssetCommandsStateModel>) {
        ctx.setState(assetCommandsDefaults.defaults);
    }
}
